import { useEffect, useState, useRef } from 'react';
import { formatSessionCode } from '@/utils/date';
import { type Bill, type BillVersion } from '@/types/bill';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, ThumbsUp, ThumbsDown, Scale, Printer } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useGetAIReview } from '@/api/bills-api';
import { printAIReview } from '@/utils/html/aiReviewHtml';

interface AiReviewDialogProps {
  bill: Bill | null;
  latestVersion: BillVersion | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface AiReviewData {
  bill_number: string;
  session_code: string;
  version: string;
  action: string;
  analysis: {
    report_umd: {
      impact_category: string;
      rationale: string;
      impacts: Array<{
        description: string;
        rating: string;
        sentiment: string;
        bill_text: string;
        citation: string;
      }>;
    };
    report_higher_ed: {
      impacts: Array<{
        description: string;
        rating: string;
        sentiment: string;
        bill_text: string;
        citation: string;
      }>;
      summary: string;
    };
  };
}

interface ImpactItemProps {
  description: string;
  rating: string;
  sentiment: string;
  billText: string;
  citation: string;
}

// Helper component for rendering individual impact items
const ImpactItem = ({ description, rating, sentiment, billText, citation }: ImpactItemProps) => {
  const sentimentStyles = {
    positive: "bg-green-100 text-green-800 print-positive",
    negative: "bg-red-100 text-red-800 print-negative",
    neutral: "bg-gray-100 text-gray-800 print-neutral"
  };

  const sentimentIcons = {
    positive: ThumbsUp,
    negative: ThumbsDown,
    neutral: Scale
  };

  const Icon = sentimentIcons[sentiment as keyof typeof sentimentIcons] || Scale;

  return (
    <div className="border rounded-md p-3 mb-3">
      <div className="flex justify-between items-start mb-2">
        <div className="font-medium">{description}</div>
        <Badge variant="secondary" className={cn(sentimentStyles[sentiment as keyof typeof sentimentStyles])}>
          <Icon className={`h-3 w-3 mr-1 ${sentiment === 'positive' ? 'thumb-up-icon' : sentiment === 'negative' ? 'thumb-down-icon' : 'scale-icon'}`} />
          Rating: {rating}
        </Badge>
      </div>

      {billText && (
        <div className="mt-2 text-sm">
          <div className="text-muted-foreground mb-1 font-semibold">Referenced Bill Text:</div>
          <div className="bg-gray-50 p-2 rounded italic text-sm">{billText}</div>
        </div>
      )}

      {citation && (
        <div className="mt-2 text-xs text-muted-foreground">
          Citation: {citation}
        </div>
      )}
    </div>
  );
};

export function AiReviewDialog({ bill, latestVersion, open, onOpenChange }: AiReviewDialogProps) {
  const [reviewData, setReviewData] = useState<AiReviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const getAIReview = useGetAIReview();
  const { toast } = useToast();
  const dialogContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setHasAttemptedFetch(false);
    }

    // For debugging: Check latestVersion structure
    if (latestVersion) {
      console.log('Latest version details:', latestVersion);
      console.log('Legislative review S3 key:', latestVersion.legislativeReviewS3Key);
    }
  }, [open, latestVersion]);

  useEffect(() => {
    const fetchReviewData = async () => {
      if (!open || !bill || !bill.session || !bill.billNumber || hasAttemptedFetch) return;

      try {
        setIsLoading(true);
        setHasAttemptedFetch(true);

        // Format the session code to match API expectations
        const formattedSession = formatSessionCode(bill.session);

        const data = await getAIReview.mutateAsync({
          session: formattedSession,
          billNumber: bill.billNumber
        });

        setReviewData(data);
      } catch (error) {
        console.error('Error fetching AI review:', error);
        if (isLoading) {
        toast({
          title: "Error",
          description: "Failed to load the AI review. Please try again.",
          variant: "destructive",
        });
      }
      // Set data to null on error to prevent continued attempts
      setReviewData(null);
    } finally {
      setIsLoading(false);
    }
  };

    fetchReviewData();
  }, [open, bill, toast, getAIReview, hasAttemptedFetch, isLoading]);


  // We no longer need browser-specific print event handlers
  // since we're using the iframe approach for all browsers

  // Function to handle printing
  const handlePrint = () => {
    // Use the new printAIReview function that uses an iframe for printing
    printAIReview(bill, reviewData);
  };

  // Don't render if there's no bill or version
  if (!bill || !latestVersion) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-3xl max-h-[90vh] overflow-y-auto AiReviewDialog__content p-3">
        {/* Print header that will appear on every page */}
        <div className="print-header">
          {bill.billNumber}: {reviewData?.action || bill?.title || 'Legislative Review'}
        </div>
        
        {/* Print footer that will appear on every page */}
        <div className="print-footer">
          Page <span className="page-number"></span> of <span className="page-count"></span>
        </div>
        
        <DialogHeader className="flex justify-between items-center pb-1 mb-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            AI Legislative Review: {bill.billNumber}
          </DialogTitle>
          <Button 
            onClick={handlePrint}
            className="flex items-center gap-2 print-visible ml-auto"
            variant="outline"
            size="sm"
          >
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </DialogHeader>

        <div className="grid gap-0 py-1">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mr-2" />
              <span>Loading AI review...</span>
            </div>
          ) : reviewData ? (
            <>
              {/* UMD Impact Card */}
              <Card className="umd-impact-card" style={{ marginBottom: '-1.5rem' }}>
                <CardHeader className="pb-1 p-2">
                  <CardTitle className="text-xl">UMD Impact Analysis</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 p-2 pt-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-sm">
                      {reviewData.analysis.report_umd.impact_category}
                    </Badge>
                  </div>

                  <div className="text-sm mb-4">
                    <h4 className="font-semibold mb-1">Rationale:</h4>
                    <p className="text-muted-foreground">{reviewData.analysis.report_umd.rationale}</p>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-semibold mb-3">Detailed Impacts:</h4>
                    {reviewData.analysis.report_umd.impacts.map((impact, index) => (
                      <ImpactItem
                        key={index}
                        description={impact.description}
                        rating={impact.rating}
                        sentiment={impact.sentiment}
                        billText={impact.bill_text}
                        citation={impact.citation}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Higher Education Impact Card */}
              <Card className="higher-ed-impact-card" style={{ marginTop: '-1.5rem' }}>
                <CardHeader className="pb-1 p-2">
                  <CardTitle className="text-xl">Higher Education Impact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 p-2 pt-0">
                  <div className="text-sm mb-4">
                    <h4 className="font-semibold mb-1">Summary:</h4>
                    <p className="text-muted-foreground">{reviewData.analysis.report_higher_ed.summary}</p>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-semibold mb-3">Detailed Impacts:</h4>
                    {reviewData.analysis.report_higher_ed.impacts.map((impact, index) => (
                      <ImpactItem
                        key={index}
                        description={impact.description}
                        rating={impact.rating}
                        sentiment={impact.sentiment}
                        billText={impact.bill_text}
                        citation={impact.citation}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              No AI review data available for this bill.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}